import { useState, useMemo } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import {
  useAgreementLinks,
  useCreateLink,
  usePatchLink,
  useDeleteLink,
  useIncomeLineSearch,
  type AgreementLink,
  type IncomeLine,
} from '../../hooks/useAgreements';

interface FordelingEntry {
  beskrivelse?: string;
  beloep?: number | null;
  prosent?: number | null;
}

interface LinkManagerProps {
  avtaleId: string;
  canEdit: boolean;
  beloepGjeldende?: number | null;
  fordelingJson?: string | null;
}

function formatCurrency(v: number | null | undefined): string {
  if (v == null) return '\u2014';
  return v.toLocaleString('nb-NO', { maximumFractionDigits: 0 }) + ' kr';
}

function parseFordelingJson(json: string | null | undefined): FordelingEntry[] {
  if (!json) return [];
  try {
    const parsed = typeof json === 'string' ? JSON.parse(json) : json;
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

/** Simple fuzzy match: check if query words appear in target string */
function fuzzyMatch(target: string, query: string): boolean {
  const t = target.toLowerCase();
  return query.toLowerCase().split(/\s+/).every((w) => t.includes(w));
}

/** Try to find a matching fordeling entry for an income line */
function suggestFromFordeling(
  line: IncomeLine,
  fordeling: FordelingEntry[],
  beloepGjeldende: number | null | undefined
): { beloep?: number; prosent?: number } | null {
  if (fordeling.length === 0) return null;

  // Try matching on AnsvarNavn, KontoNavn
  const searchTerms = [line.AnsvarNavn, line.KontoNavn].filter(Boolean).join(' ');
  if (!searchTerms) return null;

  for (const f of fordeling) {
    if (!f.beskrivelse) continue;
    if (fuzzyMatch(f.beskrivelse, searchTerms) || fuzzyMatch(searchTerms, f.beskrivelse)) {
      return {
        beloep: f.beloep ?? undefined,
        prosent: f.prosent ?? undefined,
      };
    }
  }

  // If only one fordeling entry, use it as default
  if (fordeling.length === 1) {
    const f = fordeling[0];
    return {
      beloep: f.beloep ?? (beloepGjeldende ?? undefined),
      prosent: f.prosent ?? undefined,
    };
  }

  return null;
}

// --- Allocation Summary ---

function AllocationSummary({
  beloepGjeldende,
  links,
}: {
  beloepGjeldende: number | null | undefined;
  links: AgreementLink[];
}) {
  if (beloepGjeldende == null || beloepGjeldende === 0) return null;

  const fordelt = links.reduce((sum, l) => sum + (l.Beloep_andel ?? 0), 0);
  const udisponert = beloepGjeldende - fordelt;
  const pctFordelt = Math.min((fordelt / beloepGjeldende) * 100, 100);
  const hasUnallocatedLinks = links.length > 1 && links.some((l) => l.Beloep_andel == null || l.Beloep_andel === 0);

  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.5 }}>
        <Typography variant="body2" color="text.secondary">
          Fordeling av avtalebeløp
        </Typography>
        <Typography variant="body2" fontWeight={500}>
          {formatCurrency(fordelt)} av {formatCurrency(beloepGjeldende)}
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={Math.min(pctFordelt, 100)}
        sx={{
          height: 8,
          borderRadius: 1,
          bgcolor: 'grey.200',
          '& .MuiLinearProgress-bar': {
            bgcolor: udisponert < 0 ? 'error.main' : udisponert === 0 ? 'success.main' : 'primary.main',
          },
        }}
      />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
        <Typography variant="caption" color="text.secondary">
          {pctFordelt.toFixed(0)} % fordelt
        </Typography>
        {udisponert !== 0 && (
          <Chip
            label={`${udisponert > 0 ? 'Udisponert' : 'Overfordelt'}: ${formatCurrency(Math.abs(udisponert))}`}
            size="small"
            color={udisponert < 0 ? 'error' : 'warning'}
            variant="outlined"
          />
        )}
        {udisponert === 0 && links.length > 0 && (
          <Chip label="Fullt fordelt" size="small" color="success" variant="outlined" />
        )}
      </Box>
      {hasUnallocatedLinks && (
        <Alert severity="info" sx={{ mt: 1 }} variant="outlined">
          Noen koblinger mangler beløp. Sett beløp per kobling for korrekt dekningsgrad.
        </Alert>
      )}
    </Box>
  );
}

// --- Add Link Dialog ---

function AddLinkDialog({
  avtaleId,
  beloepGjeldende,
  fordeling,
  udisponert,
  onClose,
}: {
  avtaleId: string;
  beloepGjeldende: number | null | undefined;
  fordeling: FordelingEntry[];
  udisponert: number | null;
  onClose: () => void;
}) {
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<IncomeLine | null>(null);
  const [beloepAndel, setBeloepAndel] = useState('');
  const [andelProsent, setAndelProsent] = useState('');
  const [suggestionApplied, setSuggestionApplied] = useState(false);

  const { data: searchResults, isLoading: searching } = useIncomeLineSearch(search);
  const createMutation = useCreateLink(avtaleId);

  function handleSelect(line: IncomeLine) {
    setSelected(line);
    setSuggestionApplied(false);

    // Try to suggest amount from fordeling
    const suggestion = suggestFromFordeling(line, fordeling, beloepGjeldende);
    if (suggestion?.beloep) {
      setBeloepAndel(String(suggestion.beloep));
      if (suggestion.prosent) setAndelProsent(String(suggestion.prosent));
      setSuggestionApplied(true);
    } else if (udisponert != null && udisponert > 0) {
      // Suggest remaining amount
      setBeloepAndel(String(udisponert));
    }
  }

  function handleCreate() {
    if (!selected) return;
    createMutation.mutate(
      {
        inntektslinjeID: selected.InntektslinjeID,
        beloepAndel: beloepAndel ? Number(beloepAndel) : undefined,
        andelProsent: andelProsent ? Number(andelProsent) : undefined,
      },
      { onSuccess: () => onClose() }
    );
  }

  // Auto-calculate prosent from beloep
  function handleBeloepChange(val: string) {
    setBeloepAndel(val);
    setSuggestionApplied(false);
    if (val && beloepGjeldende && beloepGjeldende > 0) {
      const pct = (Number(val) / beloepGjeldende) * 100;
      setAndelProsent(pct.toFixed(1));
    }
  }

  // Auto-calculate beloep from prosent
  function handleProsentChange(val: string) {
    setAndelProsent(val);
    setSuggestionApplied(false);
    if (val && beloepGjeldende && beloepGjeldende > 0) {
      const beloep = (Number(val) / 100) * beloepGjeldende;
      setBeloepAndel(String(Math.round(beloep)));
    }
  }

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Legg til kobling</DialogTitle>
      <DialogContent>
        <TextField
          size="small"
          type="search"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setSelected(null); setBeloepAndel(''); setAndelProsent(''); }}
          placeholder="Søk etter inntektslinje (ID, ansvar, konto)..."
          fullWidth
          autoFocus
          sx={{ mt: 1, mb: 1.5 }}
        />

        {searching && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <CircularProgress size={16} />
            <Typography variant="body2" color="text.secondary">Søker...</Typography>
          </Box>
        )}

        {!selected && searchResults?.data && searchResults.data.length > 0 && (
          <Paper variant="outlined" sx={{ mb: 1.5, maxHeight: 200, overflow: 'auto' }}>
            <List dense disablePadding>
              {searchResults.data.map((line: IncomeLine) => (
                <ListItemButton key={line.InntektslinjeID} onClick={() => handleSelect(line)}>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                        <Typography variant="caption" fontFamily="monospace">{line.InntektslinjeID}</Typography>
                        <Typography variant="body2" color="text.secondary">{line.KontoNavn || ''}</Typography>
                      </Box>
                    }
                    secondary={
                      line.AnsvarNavn
                        ? `Ansvar ${line.Ansvar} (${line.AnsvarNavn})${line.SektorNavn ? ` \u00b7 Sektor: ${line.SektorNavn}` : ''}`
                        : `Ansvar ${line.Ansvar}${line.SektorNavn ? ` \u00b7 Sektor: ${line.SektorNavn}` : ''}`
                    }
                  />
                  {line.LinkCount > 0 && (
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 1, whiteSpace: 'nowrap' }}>
                      {line.LinkCount} kobling(er)
                    </Typography>
                  )}
                </ListItemButton>
              ))}
            </List>
          </Paper>
        )}

        {!selected && search.length >= 2 && searchResults?.data?.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>Ingen treff</Typography>
        )}

        {selected && (
          <Paper sx={{ p: 1.5, bgcolor: 'grey.50', mb: 2 }}>
            <Typography variant="body2" fontWeight={500}>{selected.InntektslinjeID}</Typography>
            <Typography variant="caption" color="text.secondary">
              {selected.Enhet} &middot; Ansvar {selected.Ansvar}
              {selected.AnsvarNavn ? ` (${selected.AnsvarNavn})` : ''} &middot; Konto {selected.Konto} ({selected.KontoNavn})
            </Typography>
            <Box>
              <Button size="small" variant="text" onClick={() => { setSelected(null); setBeloepAndel(''); setAndelProsent(''); }} sx={{ mt: 0.5, p: 0, minWidth: 0 }}>
                Endre valg
              </Button>
            </Box>
          </Paper>
        )}

        {selected && (
          <>
            {suggestionApplied && (
              <Alert severity="info" icon={<AutoFixHighIcon fontSize="small" />} sx={{ mb: 1.5 }} variant="outlined">
                Beløp foreslått fra avtalens fordeling
              </Alert>
            )}
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 1 }}>
              <TextField
                size="small"
                label="Beløp andel (kr)"
                type="number"
                value={beloepAndel}
                onChange={(e) => handleBeloepChange(e.target.value)}
                inputProps={{ step: 'any' }}
              />
              <TextField
                size="small"
                label="Andel prosent"
                type="number"
                value={andelProsent}
                onChange={(e) => handleProsentChange(e.target.value)}
                inputProps={{ min: 0, max: 100, step: 0.1 }}
              />
            </Box>
            {beloepGjeldende != null && beloepGjeldende > 0 && (
              <Typography variant="caption" color="text.secondary">
                Avtalebeløp: {formatCurrency(beloepGjeldende)}
                {udisponert != null && udisponert > 0 && ` \u00b7 Udisponert: ${formatCurrency(udisponert)}`}
              </Typography>
            )}
          </>
        )}

        {createMutation.error && (
          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
            {createMutation.error.message}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose}>Avbryt</Button>
        <Button
          variant="contained"
          onClick={handleCreate}
          disabled={!selected || createMutation.isPending}
        >
          {createMutation.isPending ? 'Lagrer...' : 'Legg til'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// --- Edit Row ---

function EditableLinkRow({
  link,
  avtaleId,
  canEdit,
  beloepGjeldende,
}: {
  link: AgreementLink;
  avtaleId: string;
  canEdit: boolean;
  beloepGjeldende: number | null | undefined;
}) {
  const [editing, setEditing] = useState(false);
  const [beloep, setBeloep] = useState(String(link.Beloep_andel ?? ''));
  const [prosent, setProsent] = useState(String(link.Andel_prosent ?? ''));

  const patchMutation = usePatchLink(avtaleId);
  const deleteMutation = useDeleteLink(avtaleId);

  function handleBeloepChange(val: string) {
    setBeloep(val);
    if (val && beloepGjeldende && beloepGjeldende > 0) {
      setProsent(((Number(val) / beloepGjeldende) * 100).toFixed(1));
    }
  }

  function handleProsentChange(val: string) {
    setProsent(val);
    if (val && beloepGjeldende && beloepGjeldende > 0) {
      setBeloep(String(Math.round((Number(val) / 100) * beloepGjeldende)));
    }
  }

  function handleSave() {
    patchMutation.mutate(
      {
        linkId: link.BridgeID,
        beloepAndel: beloep ? Number(beloep) : undefined,
        andelProsent: prosent ? Number(prosent) : undefined,
      },
      { onSuccess: () => setEditing(false) }
    );
  }

  function handleDelete() {
    if (!confirm('Er du sikker på at du vil fjerne denne koblingen?')) return;
    deleteMutation.mutate(link.BridgeID);
  }

  const missingBeloep = link.Beloep_andel == null || link.Beloep_andel === 0;

  return (
    <TableRow hover>
      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{link.InntektslinjeID}</TableCell>
      <TableCell>
        <Typography variant="body2">{link.Ansvar || '\u2014'}</Typography>
        {link.AnsvarNavn && (
          <Typography variant="caption" color="text.secondary">{link.AnsvarNavn}</Typography>
        )}
      </TableCell>
      <TableCell>{link.KontoNavn || link.Konto || '\u2014'}</TableCell>
      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
        {editing ? (
          <TextField
            size="small"
            type="number"
            value={beloep}
            onChange={(e) => handleBeloepChange(e.target.value)}
            inputProps={{ step: 'any', style: { textAlign: 'right' } }}
            sx={{ width: 120 }}
          />
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
            {missingBeloep && (
              <Tooltip title="Mangler beløp — sett for korrekt dekningsgrad">
                <Chip label="!" size="small" color="warning" sx={{ minWidth: 20, height: 20, fontSize: '0.7rem' }} />
              </Tooltip>
            )}
            {formatCurrency(link.Beloep_andel)}
          </Box>
        )}
      </TableCell>
      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
        {editing ? (
          <TextField
            size="small"
            type="number"
            value={prosent}
            onChange={(e) => handleProsentChange(e.target.value)}
            inputProps={{ min: 0, max: 100, step: 0.1, style: { textAlign: 'right' } }}
            sx={{ width: 90 }}
          />
        ) : (
          link.Andel_prosent != null ? `${link.Andel_prosent.toFixed(1)}%` : '\u2014'
        )}
      </TableCell>
      {canEdit && (
        <TableCell align="right">
          {editing ? (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
              <Button
                size="small"
                variant="contained"
                onClick={handleSave}
                disabled={patchMutation.isPending}
              >
                Lagre
              </Button>
              <Button size="small" onClick={() => { setEditing(false); setBeloep(String(link.Beloep_andel ?? '')); setProsent(String(link.Andel_prosent ?? '')); }}>
                Avbryt
              </Button>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
              <IconButton size="small" onClick={() => setEditing(true)} aria-label="Rediger">
                <EditIcon fontSize="small" />
              </IconButton>
              <Button
                size="small"
                color="error"
                onClick={handleDelete}
                disabled={deleteMutation.isPending}
              >
                Fjern
              </Button>
            </Box>
          )}
        </TableCell>
      )}
    </TableRow>
  );
}

// --- Main Component ---

export function LinkManager({ avtaleId, canEdit, beloepGjeldende, fordelingJson }: LinkManagerProps) {
  const [showAdd, setShowAdd] = useState(false);
  const { data, isLoading, error } = useAgreementLinks(avtaleId);

  const links = data?.data ?? [];
  const fordeling = useMemo(() => parseFordelingJson(fordelingJson), [fordelingJson]);

  const fordelt = links.reduce((sum, l) => sum + (l.Beloep_andel ?? 0), 0);
  const udisponert = beloepGjeldende != null ? beloepGjeldende - fordelt : null;

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 3, justifyContent: 'center' }}>
        <CircularProgress size={20} />
        <Typography variant="body2" color="text.secondary">Laster koblinger...</Typography>
      </Box>
    );
  }

  if (error) {
    return <Typography variant="body2" color="error">Feil: {error.message}</Typography>;
  }

  return (
    <Box>
      <AllocationSummary beloepGjeldende={beloepGjeldende} links={links} />

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="subtitle2">{links.length} kobling(er)</Typography>
        {canEdit && (
          <Button variant="contained" size="small" onClick={() => setShowAdd(true)}>
            + Legg til kobling
          </Button>
        )}
      </Box>

      {links.length === 0 ? (
        <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>
          Ingen inntektslinjer koblet til denne avtalen.
        </Typography>
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>InntektslinjeID</TableCell>
                <TableCell>Ansvar</TableCell>
                <TableCell>Konto</TableCell>
                <TableCell align="right">Andel (kr)</TableCell>
                <TableCell align="right">Andel %</TableCell>
                {canEdit && <TableCell align="right">Handlinger</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {links.map((link: AgreementLink) => (
                <EditableLinkRow key={link.BridgeID} link={link} avtaleId={avtaleId} canEdit={canEdit} beloepGjeldende={beloepGjeldende} />
              ))}
            </TableBody>
            {links.length > 1 && (
              <TableFooter>
                <TableRow>
                  <TableCell colSpan={3} sx={{ fontWeight: 600 }}>Sum</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                    {formatCurrency(fordelt)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                    {beloepGjeldende != null && beloepGjeldende > 0
                      ? `${((fordelt / beloepGjeldende) * 100).toFixed(1)}%`
                      : '\u2014'}
                  </TableCell>
                  {canEdit && <TableCell />}
                </TableRow>
              </TableFooter>
            )}
          </Table>
        </Box>
      )}

      {showAdd && (
        <AddLinkDialog
          avtaleId={avtaleId}
          beloepGjeldende={beloepGjeldende}
          fordeling={fordeling}
          udisponert={udisponert}
          onClose={() => setShowAdd(false)}
        />
      )}
    </Box>
  );
}
